import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import Sidebar from "../components/Sidebar";
import { getEstimatePDFBase64 } from "../components/EstimatePDF";
import { useCompany } from "../hooks/useCompany";
import { BULK_BATCH_SIZE, BULK_MAX_BATCHES } from "../constants";
import { errorDetail } from "../utils/apiError";

const API_URL = process.env.REACT_APP_API_URL;
const TRIAL_ESTIMATE_LIMIT = 3;
const PAGE_SIZE_OPTIONS = [25, 50, 100];

const fmt = (n) =>
  `$${(parseFloat(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const isExpired = (est) =>
  !est.accepted &&
  est.due_date &&
  new Date(est.due_date) < new Date(new Date().toISOString().split("T")[0]);

const EstimateList = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isDemo, activeCompany } = useCompany();

  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [markingAccepted, setMarkingAccepted] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [filter, setFilter] = useState("open"); // all | open | expired | accepted
  const [sort, setSort] = useState({ key: "estimate_number", dir: "desc" });
  const [tooltip, setTooltip] = useState(null); // { text, x, y }

  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkSending, setBulkSending] = useState(false);
  const clientSearchRef = useRef(null);
  const bulkSendingRef = useRef(false);
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  const showTooltip = (e, text) => {
    const r = e.currentTarget.getBoundingClientRect();
    setTooltip({ text, x: r.left + r.width / 2, y: r.top });
  };
  const hideTooltip = () => setTooltip(null);

  const token = auth.user?.access_token;

  const fetchEstimates = useCallback(async () => {
    if (!auth.isAuthenticated) return;
    try {
      const res = await fetch(`${API_URL}/estimates/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 404) { setEstimates([]); return; }
      if (!res.ok) throw new Error(`Failed to fetch estimates: ${res.status}`);
      const data = await res.json();
      setEstimates(
        activeCompany
          ? data.filter(est => !est.company_id || est.company_id === activeCompany.company_id)
          : data
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [auth.isAuthenticated, token, activeCompany]);

  // Initial fetch
  useEffect(() => { fetchEstimates(); }, [fetchEstimates]);

  // Refetch when the owner switches back to this tab
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && !bulkSendingRef.current) fetchEstimates();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchEstimates]);

  // Poll every 30 s while the tab is visible — skipped during bulk send to avoid Lambda throttle
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible" && !bulkSendingRef.current) fetchEstimates();
    }, 30000);
    return () => clearInterval(id);
  }, [fetchEstimates]);

  useEffect(() => {
    if (!auth.isAuthenticated) return;
    fetch(`${API_URL}/clients/all`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setClients)
      .catch(() => {});
  }, [auth.isAuthenticated, token]);

  useEffect(() => {
    const handler = (e) => {
      if (clientSearchRef.current && !clientSearchRef.current.contains(e.target))
        setClientDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Block browser refresh / tab close during bulk send
  useEffect(() => {
    if (!bulkSending) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [bulkSending]);

  // Block browser back/forward navigation during bulk send
  useEffect(() => {
    if (!bulkSending) return;
    window.history.pushState(null, "", window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
      Swal.fire({
        title: "Bulk Send in Progress",
        text: "Please wait until all emails have been sent before navigating away.",
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#22c55e",
      });
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [bulkSending]);

  // ── Stats ────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const accepted    = estimates.filter(e => e.accepted);
    const open        = estimates.filter(e => !e.accepted);
    const expired     = estimates.filter(isExpired);
    const acceptedAmt = accepted.reduce((s, e) => s + (parseFloat(e.total_amount) || 0), 0);
    const openAmt     = open.reduce((s, e) => s + (parseFloat(e.total_amount) || 0), 0);
    return { accepted, open, expired, acceptedAmt, openAmt };
  }, [estimates]);

  // Bar chart — last 6 months of estimate totals
  const monthlyData = useMemo(() => {
    const now = new Date();
    const months = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
      return {
        name: d.toLocaleString("default", { month: "short" }),
        year: d.getFullYear(),
        month: d.getMonth(),
        accepted: 0,
        open: 0,
      };
    });
    estimates.forEach(est => {
      const d = new Date(est.date);
      const slot = months.find(m => m.year === d.getFullYear() && m.month === d.getMonth());
      if (!slot) return;
      if (est.accepted) slot.accepted += parseFloat(est.total_amount) || 0;
      else              slot.open     += parseFloat(est.total_amount) || 0;
    });
    return months.map(m => ({ name: m.name, Accepted: +m.accepted.toFixed(2), Open: +m.open.toFixed(2) }));
  }, [estimates]);

  // ── Sorting & filtering ──────────────────────────────────────────────────
  const clientSuggestions = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    const pool = q
      ? clients.filter(c => c.name.toLowerCase().includes(q))
      : [...clients].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 5);
    return pool.slice(0, 5);
  }, [clients, clientSearch]);

  const clientBase = useMemo(() =>
    selectedClient ? estimates.filter(e => e.client_name === selectedClient.name) : estimates,
  [estimates, selectedClient]);

  const tabCounts = useMemo(() => ({
    all:      clientBase.length,
    open:     clientBase.filter(e => !e.accepted).length,
    expired:  clientBase.filter(isExpired).length,
    accepted: clientBase.filter(e => e.accepted).length,
  }), [clientBase]);

  const filtered = useMemo(() => {
    if (filter === "accepted") return clientBase.filter(e => e.accepted);
    if (filter === "open")     return clientBase.filter(e => !e.accepted);
    if (filter === "expired")  return clientBase.filter(isExpired);
    return clientBase;
  }, [clientBase, filter]);

  const sorted = useMemo(() => {
    const { key, dir } = sort;
    return [...filtered].sort((a, b) => {
      const av = a[key] ?? "", bv = b[key] ?? "";
      if (key === "total_amount" || key === "estimate_number")
        return dir === "asc" ? Number(av) - Number(bv) : Number(bv) - Number(av);
      if (key === "accepted")
        return dir === "asc" ? (a.accepted ? 1 : -1) : (b.accepted ? 1 : -1);
      return dir === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
  }, [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginated = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);

  const pageNumbers = () => {
    const pages = [];
    const delta = 2;
    const left = Math.max(1, safePage - delta);
    const right = Math.min(totalPages, safePage + delta);
    if (left > 1) { pages.push(1); if (left > 2) pages.push("…"); }
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages) { if (right < totalPages - 1) pages.push("…"); pages.push(totalPages); }
    return pages;
  };

  const toggleSort = (key) => {
    setSort(prev => ({ key, dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc" }));
    setCurrentPage(1);
  };

  // Header checkbox controls the current page only; selections accumulate across pages
  const allPageSelected = paginated.length > 0 && paginated.every(e => selectedIds.has(e.estimate_id));
  const somePageSelected = paginated.some(e => selectedIds.has(e.estimate_id));

  // Disable bulk send when selected estimates span more than one client email
  const selectedWithEmail = sorted.filter(e => selectedIds.has(e.estimate_id) && e.client_email);
  const clientEmailMap = new Map(selectedWithEmail.map(e => [e.client_email, e.client_name]));
  const hasEmailConflict = clientEmailMap.size > 1;
  const conflictingClients = hasEmailConflict
    ? [...clientEmailMap.entries()].map(([email, name]) => ({ name, email }))
    : [];

  const toggleSelect = (id) =>
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allPageSelected) {
        paginated.forEach(e => next.delete(e.estimate_id));
      } else {
        paginated.forEach(e => next.add(e.estimate_id));
      }
      return next;
    });
  };

  const handleBulkSend = async () => {
    const toSend = sorted.filter(e => selectedIds.has(e.estimate_id));
    const withEmail = toSend.filter(e => e.client_email);
    const skippedCount = toSend.length - withEmail.length;

    if (withEmail.length === 0) {
      Swal.fire({ icon: "warning", title: "No emails to send", text: "None of the selected estimates have a client email on file." });
      return;
    }

    const maxTotal = BULK_BATCH_SIZE * BULK_MAX_BATCHES;
    const capped = withEmail.slice(0, maxTotal);
    const overLimit = withEmail.length > maxTotal;

    const batches = [];
    for (let i = 0; i < capped.length; i += BULK_BATCH_SIZE) {
      batches.push(capped.slice(i, i + BULK_BATCH_SIZE));
    }

    bulkSendingRef.current = true;
    setBulkSending(true);
    let totalSent = 0;
    let successfulBatches = 0;
    let failedCount = 0;
    let failureReason = null;

    for (let batchIdx = 0; batchIdx < batches.length; batchIdx++) {
      if (batchIdx > 0) await new Promise(r => setTimeout(r, 1000));
      const batch = batches[batchIdx];
      const recipientEmail = batch[0].client_email;
      try {
        const estimatesPayload = await Promise.all(batch.map(async (est) => {
          const doc = { ...est, business_logo_url: activeCompany?.logo_url || "" };
          const pdf_base64 = await getEstimatePDFBase64(doc);
          return { estimate_id: est.estimate_id, pdf_base64 };
        }));

        const body = JSON.stringify({ recipient_email: recipientEmail, estimates: estimatesPayload });
        let res = await fetch(`${API_URL}/estimates/bulk-send-email`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body,
        });

        if (res.status === 503) {
          await new Promise(r => setTimeout(r, 3000));
          res = await fetch(`${API_URL}/estimates/bulk-send-email`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body,
          });
        }

        if (!res.ok) throw new Error(await errorDetail(res, "Bulk send failed."));

        totalSent += batch.length;
        successfulBatches += 1;
      } catch (err) {
        failedCount += batch.length;
        // Keep the first real reason — a 429 cooldown message is far more
        // actionable than a bare count of failures.
        if (!failureReason && err?.message) failureReason = err.message;
      }
    }

    bulkSendingRef.current = false;
    setBulkSending(false);
    setSelectedIds(new Set());

    const parts = [
      `${totalSent} estimate${totalSent !== 1 ? "s" : ""} sent in ${successfulBatches} email${successfulBatches !== 1 ? "s" : ""}`,
    ];
    if (skippedCount > 0) parts.push(`${skippedCount} skipped (no email)`);
    if (overLimit) parts.push(`${withEmail.length - maxTotal} beyond ${maxTotal}-estimate limit`);
    if (failedCount > 0) parts.push(`${failedCount} failed`);

    Swal.fire({
      icon: totalSent > 0 ? "success" : "error",
      title: "Bulk Send Complete",
      text: parts.join(" · "),
      footer: failureReason || undefined,
    });
  };

  const sortIcon = (col) => (
    <span className={`ml-1 text-xs ${sort.key === col ? "text-gray-700" : "text-gray-300"}`}>
      {sort.key === col ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}
    </span>
  );

  // ── Actions ──────────────────────────────────────────────────────────────
  const handleMarkAccepted = async (estimate) => {
    const today = new Date().toISOString().split("T")[0];
    setMarkingAccepted(estimate.estimate_id);
    try {
      const res = await fetch(`${API_URL}/estimates/${estimate.estimate_id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ accepted: true, accepted_on: today }),
      });
      if (!res.ok) throw new Error("Failed to update estimate");
      const updated = await res.json();
      setEstimates(prev => prev.map(e => e.estimate_id === updated.estimate_id ? updated : e));
    } catch (err) {
      Swal.fire({ icon: "error", title: t("error"), text: err.message });
    } finally {
      setMarkingAccepted(null);
    }
  };

  const handleMarkOpen = async (estimate) => {
    setMarkingAccepted(estimate.estimate_id);
    try {
      const res = await fetch(`${API_URL}/estimates/${estimate.estimate_id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ accepted: false, accepted_on: "" }),
      });
      if (!res.ok) throw new Error("Failed to update estimate");
      const updated = await res.json();
      setEstimates(prev => prev.map(e => e.estimate_id === updated.estimate_id ? updated : e));
    } catch (err) {
      Swal.fire({ icon: "error", title: t("error"), text: err.message });
    } finally {
      setMarkingAccepted(null);
    }
  };

  const handleDelete = async (estimate) => {
    const result = await Swal.fire({
      icon: "warning",
      title: t("delete_estimate_confirm", { number: estimate.estimate_number }),
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: t("delete"),
      cancelButtonText: t("cancel"),
    });
    if (!result.isConfirmed) return;
    try {
      const res = await fetch(`${API_URL}/estimates/${estimate.estimate_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to delete estimate");
      setEstimates(prev => prev.filter(e => e.estimate_id !== estimate.estimate_id));
    } catch (err) {
      Swal.fire({ icon: "error", title: t("delete_failed"), text: err.message });
    }
  };

  const handleSendEmailFromList = async (estimate) => {
    setSendingEmail(estimate.estimate_id);
    try {
      const withLogo = (doc) => ({ ...doc, business_logo_url: activeCompany?.logo_url || "" });
      const pdf_base64 = await getEstimatePDFBase64(withLogo(estimate));
      const res = await fetch(`${API_URL}/estimates/${estimate.estimate_id}/send-email`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ recipient_email: estimate.client_email, pdf_base64 }),
      });
      if (!res.ok) throw new Error(await errorDetail(res, "Failed to send email."));
      Swal.fire({ icon: "success", title: "Email Sent", text: `Estimate #${estimate.estimate_number} emailed to ${estimate.client_email}` });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err.message });
    } finally {
      setSendingEmail(null);
    }
  };

  const handleSignOut = () => {
    auth.signOut();
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const pieData = [
    { name: "Accepted", value: stats.acceptedAmt },
    { name: "Open",     value: stats.openAmt     },
  ];
  const PIE_COLORS = ["#3b82f6", "#fbbf24"];

  const StatusBadge = ({ est }) => {
    if (est.accepted) {
      return (
        <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full">
          Accepted{est.accepted_on ? ` · ${est.accepted_on}` : ""}
        </span>
      );
    }
    if (isExpired(est))
      return <span className="bg-red-100 text-red-700 text-xs font-semibold px-2 py-0.5 rounded-full">Expired</span>;
    return <span className="bg-yellow-100 text-yellow-700 text-xs font-semibold px-2 py-0.5 rounded-full">Open</span>;
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      {tooltip && (
        <div className="fixed z-[9999] pointer-events-none"
          style={{ left: tooltip.x, top: tooltip.y - 8, transform: "translateX(-50%) translateY(-100%)" }}>
          <div className="bg-gray-800 text-white text-xs rounded-lg px-2.5 py-1.5 shadow-lg w-48 text-center leading-snug">{tooltip.text}</div>
          <div className="w-2 h-2 bg-gray-800 rotate-45 mx-auto -mt-1" />
        </div>
      )}
      <Sidebar handleSignOut={handleSignOut} disableNavigation={bulkSending}>
      <div className="flex flex-col flex-1 p-3 sm:p-6 gap-4">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">{t("estimates_title")}</h1>
            {isDemo && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 border border-yellow-200">
                {estimates.length}/{TRIAL_ESTIMATE_LIMIT} trial
              </span>
            )}
          </div>
          <button
            onClick={() => navigate("/CreateEstimate")}
            disabled={isDemo && estimates.length >= TRIAL_ESTIMATE_LIMIT}
            title={isDemo && estimates.length >= TRIAL_ESTIMATE_LIMIT ? "Subscribe to create more estimates" : undefined}
            className={`px-4 py-2 rounded-lg transition text-sm font-medium ${
              isDemo && estimates.length >= TRIAL_ESTIMATE_LIMIT
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-blue-500 text-white hover:bg-blue-600"
            }`}
          >
            {t("new_estimate_btn")}
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-20">
            <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
            </svg>
            <span className="text-gray-400 text-sm">Loading estimates…</span>
          </div>
        ) : error ? (
          <p className="text-red-500 bg-white rounded-lg p-4">{error}</p>
        ) : estimates.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-12 text-center">
            <p className="text-gray-400 text-lg mb-4">{t("no_estimates_yet")}</p>
            <button onClick={() => navigate("/CreateEstimate")} className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 text-sm">
              {t("create_first_estimate_btn")}
            </button>
          </div>
        ) : (
          <>
            {/* Stats + Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* KPI cards */}
              <div className="lg:col-span-1 grid grid-cols-2 gap-3">
                <div className="bg-white rounded-xl shadow p-4 border-l-4 border-yellow-400">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">Open</p>
                  <p className="text-xl font-bold text-yellow-700">{fmt(stats.openAmt)}</p>
                  <p className="text-xs text-gray-400 mt-1">{stats.open.length} estimate{stats.open.length !== 1 ? "s" : ""}</p>
                </div>
                <div className="bg-white rounded-xl shadow p-4 border-l-4 border-blue-400">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">Accepted</p>
                  <p className="text-xl font-bold text-blue-700">{fmt(stats.acceptedAmt)}</p>
                  <p className="text-xs text-gray-400 mt-1">{stats.accepted.length} estimate{stats.accepted.length !== 1 ? "s" : ""}</p>
                </div>
                <div className="bg-white rounded-xl shadow p-4 border-l-4 border-red-400">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">Expired</p>
                  <p className="text-xl font-bold text-red-600">{stats.expired.length}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {fmt(stats.expired.reduce((s, e) => s + (parseFloat(e.total_amount) || 0), 0))} lapsed
                  </p>
                </div>
                <div className="bg-white rounded-xl shadow p-4 border-l-4 border-gray-400">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">Total</p>
                  <p className="text-xl font-bold text-gray-700">{estimates.length}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {fmt(stats.acceptedAmt + stats.openAmt)} lifetime
                  </p>
                </div>
              </div>

              {/* Donut chart */}
              <div className="bg-white rounded-xl shadow p-4 flex flex-col items-center justify-center">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">Accepted vs Open</p>
                <ResponsiveContainer width="100%" height={150}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={68} paddingAngle={3} dataKey="value">
                      {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                    </Pie>
                    <RechartsTooltip formatter={(v) => fmt(v)} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex gap-4 text-xs text-gray-500 mt-1">
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-blue-400 inline-block" />Accepted</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-yellow-400 inline-block" />Open</span>
                </div>
              </div>

              {/* Bar chart */}
              <div className="bg-white rounded-xl shadow p-4">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-3">Last 6 Months</p>
                <ResponsiveContainer width="100%" height={150}>
                  <BarChart data={monthlyData} barSize={12} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v >= 1000 ? (v/1000).toFixed(0)+"k" : v}`} />
                    <RechartsTooltip formatter={(v) => fmt(v)} />
                    <Bar dataKey="Accepted" fill="#3b82f6" radius={[3,3,0,0]} />
                    <Bar dataKey="Open"     fill="#fbbf24" radius={[3,3,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Filter tabs + search */}
            <div className="bg-white rounded-xl shadow">
              {/* Client search bar */}
              <div className="px-4 pt-4 pb-3 border-b border-gray-100" ref={clientSearchRef}>
                <div className="relative max-w-xs">
                  <svg className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search by client…"
                    value={clientSearch}
                    onChange={(e) => {
                      setClientSearch(e.target.value);
                      setClientDropdownOpen(true);
                      setCurrentPage(1);
                      if (!e.target.value) { setSelectedClient(null); setSelectedIds(new Set()); }
                    }}
                    onFocus={() => setClientDropdownOpen(true)}
                    className="w-full border border-gray-200 rounded-lg pl-8 pr-7 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                  {clientSearch && (
                    <button
                      onClick={() => { setClientSearch(""); setSelectedClient(null); setSelectedIds(new Set()); setClientDropdownOpen(false); setCurrentPage(1); }}
                      className="absolute right-2.5 top-2 text-gray-400 hover:text-gray-600 text-sm leading-none"
                    >✕</button>
                  )}
                  {clientDropdownOpen && clientSuggestions.length > 0 && (
                    <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg w-full overflow-hidden">
                      {clientSuggestions.map(client => (
                        <button
                          key={client.client_id}
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setClientSearch(client.name);
                            setSelectedClient(client);
                            setClientDropdownOpen(false);
                            // Select only estimates visible in the current tab
                            const newBase = estimates.filter(e => e.client_name === client.name);
                            const newFiltered =
                              filter === "accepted" ? newBase.filter(e => e.accepted) :
                              filter === "open"     ? newBase.filter(e => !e.accepted) :
                              filter === "expired"  ? newBase.filter(isExpired) :
                              newBase;
                            setSelectedIds(new Set(newFiltered.map(e => e.estimate_id)));
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-blue-50 transition"
                        >
                          <p className="text-sm font-medium text-gray-800">{client.name}</p>
                          <p className="text-xs text-gray-400">{client.email || "No email on file"}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {selectedClient && (
                  <p className="text-xs text-blue-600 mt-1.5">
                    Showing {sorted.length} estimate{sorted.length !== 1 ? "s" : ""} for {selectedClient.name}
                  </p>
                )}
              </div>

              {/* Bulk send bar */}
              {selectedIds.size > 0 && (
                <div className={`flex items-center justify-between px-4 py-2 border-b ${
                  hasEmailConflict ? "bg-amber-50 border-amber-100" : "bg-blue-50 border-blue-100"
                }`}>
                  {hasEmailConflict ? (
                    <span className="text-sm text-amber-700">
                      <span className="font-medium">Multiple clients selected:</span>{" "}
                      {conflictingClients.map(c => `${c.name} (${c.email})`).join(", ")}
                      {" — filter by client to bulk send"}
                    </span>
                  ) : (
                    <span className="text-sm text-blue-700 font-medium">
                      {selectedIds.size} estimate{selectedIds.size !== 1 ? "s" : ""} selected
                    </span>
                  )}
                  <button
                    onClick={handleBulkSend}
                    disabled={bulkSending || hasEmailConflict}
                    className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg font-medium transition ${
                      hasEmailConflict
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50"
                    }`}
                  >
                    {bulkSending ? (
                      <>
                        <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                        </svg>
                        Sending…
                      </>
                    ) : `Send ${selectedIds.size} Estimate${selectedIds.size !== 1 ? "s" : ""}`}
                  </button>
                </div>
              )}

              <div className="flex items-center border-b border-gray-100">
                <div className="flex flex-1">
                  {[
                    { key: "all",      label: "All",      count: tabCounts.all      },
                    { key: "open",     label: "Open",     count: tabCounts.open     },
                    { key: "expired",  label: "Expired",  count: tabCounts.expired  },
                    { key: "accepted", label: "Accepted", count: tabCounts.accepted },
                  ].map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => { setFilter(tab.key); setCurrentPage(1); }}
                      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition -mb-px ${
                        filter === tab.key
                          ? "border-blue-500 text-blue-600"
                          : "border-transparent text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      {tab.label}
                      <span className={`text-xs rounded-full px-1.5 py-0.5 font-semibold ${
                        filter === tab.key ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-500"
                      }`}>
                        {tab.count}
                      </span>
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-1.5 px-4 text-xs text-gray-500 whitespace-nowrap">
                  <span>Per page</span>
                  <select
                    value={pageSize}
                    onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                    className="border border-gray-200 rounded px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                  >
                    {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>

              {sorted.length === 0 ? (
                <div className="py-12 text-center text-gray-400 text-sm">No estimates in this category.</div>
              ) : (
                <>
                  {/* Desktop table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="bg-gray-100 text-gray-600 text-xs uppercase tracking-wide">
                          <th className="px-4 py-3 w-8">
                            <input
                              type="checkbox"
                              checked={allPageSelected}
                              ref={el => { if (el) el.indeterminate = somePageSelected && !allPageSelected; }}
                              onChange={toggleSelectAll}
                              className="rounded border-gray-300 cursor-pointer"
                            />
                          </th>
                          <th className="px-4 py-3 text-left cursor-pointer select-none" onClick={() => toggleSort("estimate_number")}># {sortIcon("estimate_number")}</th>
                          <th className="px-4 py-3 text-left cursor-pointer select-none" onClick={() => toggleSort("client_name")}>Client {sortIcon("client_name")}</th>
                          <th className="px-4 py-3 text-left cursor-pointer select-none" onClick={() => toggleSort("date")}>Date {sortIcon("date")}</th>
                          <th className="px-4 py-3 text-left cursor-pointer select-none" onClick={() => toggleSort("due_date")}>Valid Until {sortIcon("due_date")}</th>
                          <th className="px-4 py-3 text-right cursor-pointer select-none" onClick={() => toggleSort("total_amount")}>Amount {sortIcon("total_amount")}</th>
                          <th className="px-4 py-3 text-center cursor-pointer select-none" onClick={() => toggleSort("accepted")}>Status {sortIcon("accepted")}</th>
                          <th className="px-4 py-3 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {paginated.map((est) => (
                          <tr key={est.estimate_id}
                            onClick={() => navigate(`/UpdateEstimate/${est.estimate_id}`, { state: { estimate: est, viewOnly: true } })}
                            className={`cursor-pointer hover:bg-gray-50 transition ${isExpired(est) ? "bg-red-50/40" : ""} ${selectedIds.has(est.estimate_id) ? "bg-blue-50/60" : ""}`}>
                            <td className="px-4 py-3 w-8" onClick={e => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={selectedIds.has(est.estimate_id)}
                                onChange={() => toggleSelect(est.estimate_id)}
                                className="rounded border-gray-300 cursor-pointer"
                              />
                            </td>
                            <td className="px-4 py-3 font-semibold text-gray-700">#{est.estimate_number}</td>
                            <td className="px-4 py-3 text-gray-800">{est.client_name}</td>
                            <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{est.date}</td>
                            <td className={`px-4 py-3 whitespace-nowrap ${isExpired(est) ? "text-red-500 font-medium" : "text-gray-500"}`}>
                              {est.due_date || "—"}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-gray-800">{fmt(est.total_amount)}</td>
                            <td className="px-4 py-3 text-center"><StatusBadge est={est} /></td>
                            <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-center gap-1.5 flex-wrap">
                                {est.accepted ? (
                                  <>
                                    <button onClick={() => handleMarkOpen(est)} disabled={markingAccepted === est.estimate_id}
                                      className="text-xs px-2.5 py-1 rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300 font-medium disabled:opacity-50">
                                      {markingAccepted === est.estimate_id ? "…" : "Reopen"}
                                    </button>
                                    <button
                                      onMouseEnter={(e) => showTooltip(e, "Reopen the estimate before deleting")}
                                      onMouseLeave={hideTooltip}
                                      className="text-xs px-2.5 py-1 rounded-md bg-gray-100 text-gray-400 font-medium cursor-not-allowed">{t("delete")}</button>
                                  </>
                                ) : (
                                  <>
                                    <button onClick={() => navigate(`/UpdateEstimate/${est.estimate_id}`, { state: { estimate: est, viewOnly: false } })}
                                      className="text-xs px-2.5 py-1 rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300 font-medium">Edit</button>
                                    <button onClick={() => handleMarkAccepted(est)} disabled={markingAccepted === est.estimate_id}
                                      className="text-xs px-2.5 py-1 rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 font-medium disabled:opacity-50">
                                      {markingAccepted === est.estimate_id ? "…" : "Accept"}
                                    </button>
                                    {est.client_email ? (
                                      <button onClick={() => handleSendEmailFromList(est)} disabled={sendingEmail === est.estimate_id}
                                        className="text-xs px-2.5 py-1 rounded-md bg-green-50 text-green-600 hover:bg-green-100 font-medium disabled:opacity-50">
                                        {sendingEmail === est.estimate_id ? "…" : "Email"}
                                      </button>
                                    ) : (
                                      <button
                                        onMouseEnter={(e) => showTooltip(e, "Add a client email to this estimate to enable sending")}
                                        onMouseLeave={hideTooltip}
                                        className="text-xs px-2.5 py-1 rounded-md bg-gray-100 text-gray-400 font-medium cursor-not-allowed">Email</button>
                                    )}
                                    <button onClick={() => handleDelete(est)}
                                      className="text-xs px-2.5 py-1 rounded-md bg-red-50 text-red-500 hover:bg-red-100 font-medium">{t("delete")}</button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile cards */}
                  <div className="md:hidden divide-y divide-gray-100">
                    {paginated.map((est) => (
                      <div key={est.estimate_id}
                        onClick={() => navigate(`/UpdateEstimate/${est.estimate_id}`, { state: { estimate: est, viewOnly: true } })}
                        className={`p-4 cursor-pointer ${isExpired(est) ? "bg-red-50/40" : ""} ${selectedIds.has(est.estimate_id) ? "bg-blue-50/60" : ""}`}>
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-start gap-2">
                            <div onClick={e => e.stopPropagation()} className="mt-0.5">
                              <input
                                type="checkbox"
                                checked={selectedIds.has(est.estimate_id)}
                                onChange={() => toggleSelect(est.estimate_id)}
                                className="rounded border-gray-300 cursor-pointer"
                              />
                            </div>
                            <div>
                              <p className="text-sm font-bold text-gray-800">#{est.estimate_number} · {est.client_name}</p>
                              <p className="text-xs text-gray-400 mt-0.5">
                                {est.date}{est.due_date ? ` · Valid until ${est.due_date}` : ""}
                                {isExpired(est) && <span className="ml-1 text-red-500 font-medium">Expired</span>}
                              </p>
                            </div>
                          </div>
                          <div className="text-right ml-3 flex-shrink-0">
                            <p className="text-base font-bold text-gray-800">{fmt(est.total_amount)}</p>
                            <StatusBadge est={est} />
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                          {est.accepted ? (
                            <>
                              <button onClick={() => handleMarkOpen(est)} disabled={markingAccepted === est.estimate_id}
                                className="flex-1 text-xs py-1.5 rounded-lg bg-gray-200 text-gray-700 font-medium hover:bg-gray-300 disabled:opacity-50">
                                {markingAccepted === est.estimate_id ? "…" : "Reopen"}
                              </button>
                              <button disabled className="flex-1 text-xs py-1.5 rounded-lg bg-gray-100 text-gray-400 font-medium cursor-not-allowed">{t("delete")}</button>
                            </>
                          ) : (
                            <>
                              <button onClick={() => navigate(`/UpdateEstimate/${est.estimate_id}`, { state: { estimate: est, viewOnly: false } })}
                                className="flex-1 text-xs py-1.5 rounded-lg bg-gray-200 text-gray-700 font-medium hover:bg-gray-300">Edit</button>
                              <button onClick={() => handleMarkAccepted(est)} disabled={markingAccepted === est.estimate_id}
                                className="flex-1 text-xs py-1.5 rounded-lg bg-blue-50 text-blue-600 font-medium hover:bg-blue-100 disabled:opacity-50">
                                {markingAccepted === est.estimate_id ? "…" : "Accept"}
                              </button>
                              {est.client_email && (
                                <button onClick={() => handleSendEmailFromList(est)} disabled={sendingEmail === est.estimate_id}
                                  className="flex-1 text-xs py-1.5 rounded-lg bg-green-50 text-green-600 font-medium hover:bg-green-100 disabled:opacity-50">
                                  {sendingEmail === est.estimate_id ? "…" : "Email"}
                                </button>
                              )}
                              <button onClick={() => handleDelete(est)}
                                className="flex-1 text-xs py-1.5 rounded-lg bg-red-50 text-red-500 font-medium hover:bg-red-100">{t("delete")}</button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination controls */}
                  {totalPages > 1 && (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-4 py-3 border-t border-gray-100 gap-2 text-sm text-gray-600">
                      <span>
                        Showing {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, sorted.length)} of {sorted.length}
                      </span>
                      <div className="flex items-center gap-1">
                        <button onClick={() => setCurrentPage(1)} disabled={safePage === 1}
                          className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-100" title="First">«</button>
                        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={safePage === 1}
                          className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-100" title="Previous">‹</button>
                        {pageNumbers().map((p, i) =>
                          p === "…" ? (
                            <span key={`e${i}`} className="px-2 py-1">…</span>
                          ) : (
                            <button key={p} onClick={() => setCurrentPage(p)}
                              className={`px-3 py-1 rounded border ${p === safePage ? "bg-blue-500 text-white border-blue-500" : "border-gray-200 hover:bg-gray-100"}`}>
                              {p}
                            </button>
                          )
                        )}
                        <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}
                          className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-100" title="Next">›</button>
                        <button onClick={() => setCurrentPage(totalPages)} disabled={safePage === totalPages}
                          className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-100" title="Last">»</button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        <Link to="/" className="self-start">
          <button className="bg-gray-500 text-white py-2 px-6 rounded-lg hover:bg-gray-600 transition text-sm">
            {t("back_to_dashboard_btn")}
          </button>
        </Link>
      </div>
      </Sidebar>
    </div>
  );
};

export default EstimateList;
